'use client';

import React, { Suspense } from 'react';
import { useRouter } from 'next/navigation';
import { SURFACE_DEFAULT_DATE_FILTER } from '@/constants/feed-filters';
import { SEO_FIRST_PAGE_TAKE } from '@/constants/feed-pagination';

import type { HomeFeedPage, HomeFeedParams } from '@repo/types';

import { useResponsiveBlockSize } from '@/hooks/use-responsive-block-size';
import {
  FeedPageContainer,
  HomeFeed,
  HomeFeedFilterBar,
  useHomeFeedUrlState,
} from '@/components/feed';

interface ChannelFeedViewProps {
  channelHandle: string;
  channelName: string;

  initialPage: HomeFeedPage | null;
}

const Inner: React.FC<ChannelFeedViewProps> = ({
  channelHandle,
  channelName,
  initialPage,
}) => {
  const router = useRouter();
  // Channel surface default is All time (Aki decision): the SSR seed, the
  // resolved client default, and the date dropdown all read the same map
  // entry, so the dropdown always shows the window the feed actually fetched.
  const { filters, setFilters } = useHomeFeedUrlState(
    'all',
    SURFACE_DEFAULT_DATE_FILTER.channel,
  );
  const blockSize = useResponsiveBlockSize();

  const params: HomeFeedParams = {
    page: 1,
    pageSize: SEO_FIRST_PAGE_TAKE,
    blockSize,
    onlyVideos: true,
    channelHandle,
    categories: filters.categories,
    dateFilter: filters.dateFilter,
  };

  const seedMatches =
    (filters.categories?.length ?? 0) === 0 &&
    filters.dateFilter === SURFACE_DEFAULT_DATE_FILTER.channel;

  // Only a real refinement (category, or a non-default date window) counts as
  // an active filter. At the surface default an empty feed renders the plain
  // empty state, not a clear-all tile that would no-op.
  const filtersActive = !seedMatches;

  return (
    <FeedPageContainer>
      <HomeFeedFilterBar
        filters={filters}
        onFiltersChange={setFilters}
        showKinds={false}
        // NOT capped here, unlike the hubs and /brands: the cap drops
        // ALL_TIME, and ALL_TIME is this surface's own default
        // (SURFACE_DEFAULT_DATE_FILTER.channel). Capping it makes the
        // dropdown unable to display the default — it falls back to the
        // first option, "Today" — and unable to return to it once the
        // visitor picks another window.
        capDateOptions={false}
        scopeBadge={{
          label: channelName,
          onRemove: () => router.push('/youtube'),
        }}
      />
      <HomeFeed
        params={params}
        initialPage={seedMatches ? initialPage : null}
        filtersActive={filtersActive}
        alwaysShowClearAll
        // Clearing filters resets to THIS surface's default (All time) and
        // stays on the channel page; leaving the page is the scope badge's
        // job, not the clear-all tile's.
        onClearAll={() => setFilters({})}
        // Channel pages are their own feed segment — without this every
        // channel-page load reported feed_kind='home'.
        feedKind="channel"
      />
    </FeedPageContainer>
  );
};

export const ChannelFeedView: React.FC<ChannelFeedViewProps> = (props) => (
  <Suspense fallback={null}>
    <Inner {...props} />
  </Suspense>
);
